import React, { JSX, useCallback, useContext, useEffect, useReducer, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { Routes } from '../../app/Routes';
import { BlankPage } from '../../components/BlankPage/BlankPage';
import { Button } from '../../components/Button/Button';
import { Input } from '../../components/Input/Input';
import { formReducer } from '../../components/form/formReducer';
import { localized } from '../../config/localization';
import { confirmSignUp, verifyCurrentUserAttribute } from '../../services/Auth';
import { AuthContext, AuthContextProps } from '../../services/AuthContext';
import { BlechconAuthToken } from '../../services/api/Internal/Api';

export function EmailConfirmationPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();

  const [state, dispatch] = useReducer(formReducer, {
    values: {
      passcode: '',
    },
    errors: {
      passcode: null,
    },
    valid: false,
  });

  const { setLoginUser } = useContext<AuthContextProps>(AuthContext);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!location.state) {
      navigate(Routes.LOGIN);
    }
  }, [navigate]);

  const resendConfirmationCode = async () => {
    try {
      await verifyCurrentUserAttribute();
    } catch (error) {
      console.error(error);
      toast.error(String(error));
    }
  };

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setIsSubmitting(true);
      const userCredentials = location.state as { username: string; password: string };

      confirmSignUp(userCredentials.username, userCredentials.password, state.values.passcode)
        .then(({ user, token }) => {
          BlechconAuthToken.storeToken(token);
          setLoginUser(user);

          navigate(Routes.WELCOME_PAGE);
        })
        .catch((error) => dispatch({ errors: { passcode: error.message } }))
        .finally(() => setIsSubmitting(false));
    },
    [navigate, setLoginUser, state.values.passcode]
  );

  return (
    <BlankPage title={localized.EMAIL_CONFIRMATION_ENTER_PASSCODE}>
      <form onSubmit={handleSubmit}>
        <Input
          autoFocus
          name="passcode"
          type="text"
          placeholder={localized.PASSCODE}
          errors={state.errors}
          values={state.values}
          onChange={(event) => {
            const value = event.target.value.trim();
            dispatch({
              values: { passcode: value },
              errors: {
                passcode: (value.length || 0) < 6 ? localized.VERIFICATION_CODE : null,
              },
            });
          }}
          dataCy="emailConfirmationPasscodeField"
        />

        <BlankPage.Links title={localized.FORGOT_PASSWORD} onClick={resendConfirmationCode} />

        <BlankPage.Buttons>
          <Button type="submit" disabled={!state.valid || isSubmitting} dataCy="emailConfirmationSubmitButton">
            {localized.CONFIRM}
          </Button>
        </BlankPage.Buttons>
      </form>
    </BlankPage>
  );
}
